/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_RowVirtualizer,
  MRT_TableOptions,
  MRT_RowData,
} from "mantine-react-table"
import { useTranslation } from "react-i18next"
import { useMrtLocalization } from "../use-mrt-localization"
import {
  PageRequest,
  Pagination,
} from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { useBreakpoints } from "../../../../utils/use-breakpoints"
import { useDefaultInfiniteQuery } from "../../../../utils/useDefaultInfiniteQuery"

interface Props<
  TQueryFn extends (...args: any[]) => Promise<Pagination<any>>,
  TData extends MRT_RowData = Awaited<ReturnType<TQueryFn>>["data"],
> {
  queryFn: TQueryFn
  getParams: (pageRequest: PageRequest) => Parameters<TQueryFn>[0]
  onClickRow?: (row: TData) => void
  tableOptions: Omit<MRT_TableOptions<TData>, "data">
}

export function InfiniteScrollingTable<
  TQueryFn extends (...args: any[]) => Promise<Pagination<any>>,
  TData extends MRT_RowData = Awaited<ReturnType<TQueryFn>>["data"],
>({ queryFn, getParams, onClickRow, tableOptions }: Props<TQueryFn, TData>) {
  const { t } = useTranslation("app_server")

  const tableContainerRef = React.useRef<HTMLDivElement>(null)
  const rowVirtualizerInstanceRef =
    React.useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null)

  const { data, fetchNextPage, isFetching, isLoading, isError } =
    useDefaultInfiniteQuery(
      queryFn as (...args: any[]) => Promise<Pagination<any>>,
      getParams
    )

  const flatData: TData[] = React.useMemo(
    () => data?.pages.flatMap((page) => page.data as unknown as TData) ?? [],
    [data, isFetching]
  )

  const totalDBRowCount = data?.pages?.[0]?.totalCount ?? 0
  const totalFetched = flatData.length

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  React.useEffect(() => {
    tableContainerRef.current &&
      fetchMoreOnBottomReached(tableContainerRef.current)
  }, [fetchMoreOnBottomReached])

  const { locale } = useMrtLocalization()

  const { xs, sm } = useBreakpoints()
  const isMobile = xs || sm

  const table = useMantineReactTable({
    enableStickyHeader: true,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => onClickRow && onClickRow(row.original),
      style: { cursor: onClickRow ? "pointer" : undefined },
    }),
    ...tableOptions,
    data: flatData,
    enablePagination: false,
    enableRowVirtualization: true,
    mantineTableContainerProps: {
      ref: tableContainerRef,
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
      onScroll: (event: React.UIEvent<HTMLDivElement>) =>
        fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    mantinePaperProps: {
      style: {
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    enableBottomToolbar: !isMobile,
    mantineBottomToolbarProps: () => ({
      fz: "sm",
      style: { alignContent: "center" },
    }),
    renderBottomToolbarCustomActions: () =>
      t("VirtualTable_Footer", {
        count: totalFetched,
        total: totalDBRowCount,
      }),
    mantineTableBodyCellProps: () => ({
      fw: 400,
      style: { textRendering: "optimizeLegibility" },
    }),
    state: {
      ...tableOptions.state,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showLoadingOverlay: isLoading,
      density: "xs",
    },
    rowVirtualizerInstanceRef,
    rowVirtualizerOptions: { overscan: 20 },
    localization: locale,
    layoutMode: "grid-no-grow",
  })

  return <MantineReactTable table={table} />
}
